import React, { useRef, useState, useEffect } from "react";
import { DimensionValue, StyleSheet, Pressable, ViewStyle } from "react-native";
import NSText from "./NSText";
import { AntDesign } from "@expo/vector-icons";
import ClearFilter from "./ClearFilter";
import GlobalNestedPicker, { NestedDataType } from "./GlobalNestedPicker";
import { createPortal } from "react-dom";

interface PropTypes {
  dropDownWidth?: DimensionValue;
  defaultTitle?: string;
  data: NestedDataType[];
  selectedId: string;
  selectedItem: string;
  canClearFilter: boolean;
  onItemPress: (value: any, index: number) => void;
  onClearFilter: () => void;
  hasSearch?: boolean;
  searchPlaceholder?: string;
  positionY?: 'top' | 'bottom'
  positionX?: 'left' | 'right' | 'middle';
  direction?: 'up' | 'down';
  borderWidth?: number;
  paddingVertical?: number;
  paddingHorizonal?: number;
  containerFontColor?: string;
  containerFontSize?: number;
  styles?: ViewStyle;
  disable?: boolean;
  containerColor?: string;
  useAbsolutePositioning?: boolean;
  enableOutsideClickToDismissDropDown?: boolean;
  minDropDownWidth?: number;
  hasDeepSearch?: boolean;
  allowParentSelection?: boolean;
}

const SingleSelectNestedDropDown = ({
  dropDownWidth,
  defaultTitle,
  data,
  selectedId,
  selectedItem,
  canClearFilter,
  onItemPress,
  onClearFilter,
  positionX,
  positionY,
  direction,
  borderWidth,
  paddingVertical,
  paddingHorizonal,
  containerFontColor,
  containerFontSize,
  styles,
  disable = false,
  containerColor,
  useAbsolutePositioning = false,
  enableOutsideClickToDismissDropDown = false,
  hasSearch,
  searchPlaceholder,
  minDropDownWidth,
  hasDeepSearch,
  allowParentSelection,
}: PropTypes) => {
  const ref = useRef<typeof Pressable>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [ state,setState ] = useState({visibility: false, width:0, x: 0, y:0, height:0});
  const [runtimeHeight,setRuntimeHeight] =  useState(50);

  // Handle outside click to close dropdown
  useEffect(() => {
    if (!enableOutsideClickToDismissDropDown || !state.visibility) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is within the container
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }
      
      // When using portal (useAbsolutePositioning = false), also check if click is within the dropdown
      if (!useAbsolutePositioning) {
        // Look for the dropdown element in the portal
        const dropdownElement = document.querySelector('[data-dropdown-portal="true"]');
        if (dropdownElement && dropdownElement.contains(target)) {
          return;
        }
      }
      
      setState({...state, visibility: false});
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [state.visibility, enableOutsideClickToDismissDropDown, state, useAbsolutePositioning]);

  const dropDownOnPress = () => {
    ref.current.measure((_, _1, width, height, pageX, pageY) => {
      setState({visibility: !state.visibility, width: width, x: pageX, y: pageY, height: height});
    });
  };

  const positionCord = ()=>{
    let x, y;
    
    if (useAbsolutePositioning) {
      // For absolute positioning, use relative coordinates
      switch(positionX){
        case 'left':
          x = -state.width;
          break;
        case 'middle':
          x = 0;
          break;
        case 'right' :
          x = state.width;
          break;
        default:
          x = 0;
          break;
      }
      switch(positionY){
        case 'bottom':
          if(direction === 'down'){
            y = state.height;
          }
          else{
            y = state.height - runtimeHeight;
          }
          break;
        case 'top':
          if(direction === 'down'){
            y = 0;
          }
          else{
            y = -runtimeHeight;
          }
          break;
        default:
          y = state.height;
          break;
      }
    } else {
      // For fixed positioning, use absolute coordinates
      switch(positionX){
        case 'left':
          x = state.x - state.width;
          break;
        case 'middle':
          x = state.x;
          break;
        case 'right' :
          x = state.x + state.width;
          break;
        default:
          x = state.x;
          break;
      }
      switch(positionY){
        case 'bottom':
          if(direction === 'down'){
            y = state.y + state.height;
          }
          else{
            y = state.y + state.height - runtimeHeight;
          }
          break;
        case 'top':
          if(direction === 'down'){
            y = state.y
          }
          else{
            y = state.y - runtimeHeight
          }
          break;
        default:
          y = state.y + state.height;
          break;
      }
    }
    
    return {x,y}
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Pressable
        ref={ref}
        style={[
          style?.container,
          {
            maxWidth: dropDownWidth || 200,
            minWidth: minDropDownWidth || 0,
            borderWidth: borderWidth || 0,
            borderColor: canClearFilter ? "#6300A31A" : "#6300A333",
            backgroundColor: canClearFilter ? "#6300A31A" : containerColor ?? "",
            paddingHorizontal: paddingHorizonal || 0,
            paddingVertical: paddingVertical || 0,
          },
          styles,
        ]}
        onPress={dropDownOnPress}
        disabled={disable}
      >
        <NSText
          style={{
            flexShrink: 1,
            maxWidth: "99%",
            fontSize: containerFontSize || 14,
            color: containerFontColor || "#303030",
          }}
          props={{ numberOfLines: 1 }}
          text={!!selectedItem ? selectedItem : defaultTitle}
        />
        
        <AntDesign name={direction === 'up' ? 'caret-up' : "caret-down"} size={10} color={containerFontColor || "#6300A3"} />
        {canClearFilter ? <ClearFilter onClearFilter={onClearFilter} /> : null}
      </Pressable>
      {state.visibility && (
        useAbsolutePositioning ? (
          <GlobalNestedPicker
            width={state?.width}
            coords={{ x: 0, y: state.height }}
            data={data}
            selectedId={selectedId}
            onItemPress={onItemPress}
            closeDropDown= {()=>{setState({...state, visibility: false})}}
            setRuntimeHeight={setRuntimeHeight}
            hasSearch={hasSearch}
            searchPlaceholder={searchPlaceholder}
            hasDeepSearch={hasDeepSearch}
            allowParentSelection={allowParentSelection}
          />
        ) : (
          createPortal(
            <div data-dropdown-portal="true" style={{ zIndex: 3000 }}>
              <GlobalNestedPicker
                width={state?.width}
                coords={positionCord()}
                data={data}
                selectedId={selectedId}
                onItemPress={onItemPress}
                closeDropDown= {()=>{setState({...state, visibility: false})}}
                setRuntimeHeight={setRuntimeHeight}
                hasSearch={hasSearch}
                searchPlaceholder={searchPlaceholder}
                hasDeepSearch={hasDeepSearch}
                allowParentSelection={allowParentSelection}
              />
            </div>, document.body)
        )
      )}
    </div>
  );
};

export default SingleSelectNestedDropDown;

const style = StyleSheet?.create?.({
  container: {
    flexDirection: "row",
    gap: 8,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "space-between",
  },
});
